import {
  Button,
  Group,
  Modal,
  Stack,
  Text,
  Title,
  Tooltip,
  Loader,
} from "@mantine/core"
import { MonthPickerInput } from "@mantine/dates"
import { useForm } from "@mantine/form"
import { IconFilePlus, IconRefresh } from "@tabler/icons-react"
import { useNavigate } from "react-router-dom"

import { DigestResponses } from "@costory/types/endpoints/digest"
import { DatePresetDigest } from "@costory/types/prisma-client"

import { DEFAULT_DATE_FORMAT } from "@costory/shared/const"
import dayjs from "@costory/shared/dayjs"

import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import { useAvailableDataDigestQueryWithRefresh } from "@costory/front/queries/digest"

function CreateDigestForm({
  availableData,
  isLoading,
  isRefetching,
  refetch,
}: {
  availableData: DigestResponses.AvailableDigests
  isLoading: boolean
  isRefetching: boolean
  refetch: () => void
}) {
  const navigate = useNavigate()
  const form = useForm({
    initialValues: {
      date: dayjs()
        .local()
        .subtract(1, "month")
        .startOf("month")
        .format(DEFAULT_DATE_FORMAT),
      datePreset: "LAST_MONTH" as DatePresetDigest,
    },
  })

  const handleCreate = () => {
    const { date, datePreset } = form.values
    navigate(`new/${date}/${datePreset ?? "LAST_MONTH"}`)
  }

  // Only last month is supported, so max date is end of previous month
  const maxDateForPicker = dayjs()
    .local()
    .subtract(1, "month")
    .endOf("month")
    .format(DEFAULT_DATE_FORMAT)

  // Check if selected month is available in availableData
  const isSelectedMonthAvailable = availableData
    .filter((elm) => elm.datePreset === form.values.datePreset)
    .map((elm) => elm.startPeriod)
    .includes(form.values.date)

  // Determine if we should show refresh state
  // Show refresh when data is not available and we're actively refreshing
  const shouldShowRefresh =
    !isSelectedMonthAvailable && (isRefetching || isLoading)

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        Select a month to create a new digest.
      </Text>

      <Stack gap="xs">
        <Text size="sm" fw={500}>
          Month
        </Text>
        <MonthPickerInput
          value={form.values.date}
          onChange={(date) =>
            form.setFieldValue(
              "date",
              date ||
                dayjs()
                  .local()
                  .subtract(1, "month")
                  .startOf("month")
                  .format(DEFAULT_DATE_FORMAT),
            )
          }
          placeholder="Select month"
          maxDate={maxDateForPicker}
          data-intercom-target="costory-tour-digest-month-picker"
        />
      </Stack>

      <Group justify="flex-end" mt="md">
        {!isSelectedMonthAvailable && (
          <Button
            variant="outline"
            leftSection={<IconRefresh size={16} />}
            onClick={refetch}
            loading={isRefetching}
            data-intercom-target="costory-tour-digest-refresh-data"
          >
            Refresh
          </Button>
        )}
        <Tooltip
          label="Digest data is not available."
          disabled={isSelectedMonthAvailable}
          withArrow
        >
          <Button
            leftSection={
              shouldShowRefresh ? (
                <Loader size={16} />
              ) : (
                <IconFilePlus size={16} />
              )
            }
            onClick={handleCreate}
            disabled={!isSelectedMonthAvailable}
            data-intercom-target="costory-tour-digest-create-button"
          >
            {shouldShowRefresh ? "Refreshing..." : "Create Digest"}
          </Button>
        </Tooltip>
      </Group>
    </Stack>
  )
}

export function CreateDigestModal({
  opened,
  close,
}: {
  opened: boolean
  close: () => void
}) {
  // Use the new query with refresh functionality
  // Enable refresh when modal is opened
  const query = useAvailableDataDigestQueryWithRefresh(opened)

  return (
    <Modal
      title={<Title order={3}>Create New Digest</Title>}
      size="md"
      opened={opened}
      onClose={close}
    >
      <QueryWrapper query={query} allowEmptyArray>
        {({ data }) => (
          <CreateDigestForm
            availableData={data}
            isLoading={query.isLoading}
            isRefetching={query.isRefetching}
            refetch={query.refetch}
          />
        )}
      </QueryWrapper>
    </Modal>
  )
}
